import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMsal } from '@azure/msal-react';
import getToken from '../auth/getToken';
import PageHeader from './common/PageHeader';
import StatsDefinitions from './Stats/StatsDefinitions';
import WaitStatsPanel from './Stats/WaitStatsPanel';

function formatMinutes(value) {
  return `${Number(value || 0).toFixed(1)} Min.`;
}
function formatSeconds(value) {
  return `${Number(value || 0).toFixed(1)} Sek.`;
}
function formatFactor(value) {
  const numeric = Number(value || 0);
  if (!numeric) return '–';
  return `${numeric.toFixed(2)}x`;
}

function MetricItem({ icon, title, value, meta }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '44px minmax(0, 1fr)',
        gap: 1.25,
        alignItems: 'center',
        p: 1.5,
        borderRadius: 2.5,
        backgroundColor: alpha(theme.palette.common.white, 0.62),
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.08),
        transition: 'background-color 160ms ease, transform 160ms ease',
        '&:hover': {
          backgroundColor: alpha(theme.palette.common.white, 0.9),
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', lineHeight: 1.25 }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '1.05rem', sm: '1.2rem' },
            fontWeight: 700,
            lineHeight: 1.15,
            mt: 0.15,
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.3, lineHeight: 1.25 }}
        >
          {meta}
        </Typography>
      </Box>
    </Box>
  );
}

export default function Stats() {
  const { instance } = useMsal();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [recent, setRecent] = useState({});
  const [waitStats, setWaitStats] = useState({});
  const [modelStats, setModelStats] = useState([]);
  const [error, setError] = useState('');

  // Wer die Seite sehen darf, entscheidet das Backend (ADMIN_USERS); der
  // Admin-Bereich zeigt sie ohnehin nur Admins.
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setError('');
        setLoading(true);
        const token = await getToken(instance, [
          `${process.env.REACT_APP_CLIENT_ID}/.default`,
        ]);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.status === 403) {
          throw new Error('Die Statistiken sind nur für Admins freigeschaltet.');
        }
        if (!response.ok) {
          throw new Error('Statistiken konnten nicht geladen werden.');
        }
        const data = await response.json();
        setSummary(data.summary || {});
        setRecent(data.recent || {});
        setWaitStats(data.wait_stats || {});
        setModelStats(data.model_stats || []);
      } catch (fetchError) {
        console.error('Error fetching stats data:', fetchError);
        setError(fetchError.message || 'Statistiken konnten nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [instance]);


  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <PageHeader
        overline="Admin"
        title="Statistiken"
        subtitle="Nutzung, Wartezeiten und Verarbeitungstempo im Überblick."
      />

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: 3 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', lg: '1.7fr 1fr' },
              alignItems: 'start',
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.common.white, 0.78),
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '0.98rem' }}>
                Kennzahlen
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.35, mb: 1.5 }}
              >
                Die wichtigsten Werte für Umfang, Wartezeit und Verarbeitung.
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gap: 1.25,
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(3, minmax(0, 1fr))',
                  },
                }}
              >
                <MetricItem
                  icon={<QueryStatsOutlinedIcon />}
                  title="Gesamtumfang"
                  value={formatMinutes(summary.total_minutes)}
                  meta={`${summary.total_count || 0} Transkriptionen`}
                />
                <MetricItem
                  icon={<CalendarMonthOutlinedIcon />}
                  title="Letzte 7 Tage"
                  value={formatMinutes(recent.days_7?.minutes)}
                  meta={`${recent.days_7?.count || 0} Transkriptionen`}
                />
                <MetricItem
                  icon={<CalendarMonthOutlinedIcon />}
                  title="Letzte 30 Tage"
                  value={formatMinutes(recent.days_30?.minutes)}
                  meta={`${recent.days_30?.count || 0} Transkriptionen`}
                />
                <MetricItem
                  icon={<AccessTimeOutlinedIcon />}
                  title="Ø Wartezeit bis Start"
                  value={formatSeconds(summary.avg_wait_time)}
                  meta="Queue bis Verarbeitungsbeginn"
                />
                <MetricItem
                  icon={<MemoryOutlinedIcon />}
                  title="Ø Backend-Laufzeit"
                  value={formatSeconds(summary.avg_processing_time)}
                  meta="Start bis Abschluss im Backend"
                />
                <MetricItem
                  icon={<SpeedOutlinedIcon />}
                  title="Tempo gegenüber Echtzeit"
                  value={formatFactor(summary.realtime_factor)}
                  meta={
                    summary.realtime_factor
                      ? `${formatSeconds(summary.seconds_per_audio_minute)} je Audio-Minute`
                      : 'Noch keine Laufzeitdaten'
                  }
                />
              </Box>
            </Paper>

            <StatsDefinitions />
          </Box>

          <WaitStatsPanel waitStats={waitStats} />

          {modelStats.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 4, md: 5 },
                borderRadius: 3,
                textAlign: 'center',
                backgroundColor: alpha(theme.palette.common.white, 0.78),
              }}
            >
              <AudioFileOutlinedIcon
                sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }}
              />
              <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Noch keine Statistikdaten vorhanden
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: '0.9rem' }}>
                Sobald Transkriptionen verarbeitet wurden, erscheinen hier die
                ersten Kennzahlen.
              </Typography>
            </Paper>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: alpha(theme.palette.common.white, 0.8),
              }}
            >
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.98rem' }}>
                    Vergleich nach Modell
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.35 }}
                  >
                    Umfang, Wartezeit und Verarbeitungstempo je Modell.
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Häufigstes Modell:{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {summary.top_model || '–'}
                  </Box>
                </Typography>
              </Box>

              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 920 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 1.2, whiteSpace: 'nowrap' }}>Modell</TableCell>
                      <TableCell align="right" sx={{ py: 1.2, whiteSpace: 'nowrap' }}>Jobs</TableCell>
                      <TableCell align="right" sx={{ py: 1.2, whiteSpace: 'nowrap' }}>Audio gesamt</TableCell>
                      <TableCell align="right" sx={{ py: 1.2, whiteSpace: 'nowrap' }}>Ø Dateilänge</TableCell>
                      <TableCell align="right" sx={{ py: 1.2, whiteSpace: 'nowrap' }}>Ø Warten bis Start</TableCell>
                      <TableCell align="right" sx={{ py: 1.2, whiteSpace: 'nowrap' }}>Ø Backend-Laufzeit</TableCell>
                      <TableCell align="right" sx={{ py: 1.2, whiteSpace: 'nowrap' }}>1 Min. Audio braucht</TableCell>
                      <TableCell align="right" sx={{ py: 1.2, whiteSpace: 'nowrap' }}>Tempo ggü. Echtzeit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modelStats.map((stat) => (
                      <TableRow
                        key={stat.model}
                        sx={{
                          transition: 'background-color 160ms ease',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        <TableCell component="th" scope="row" sx={{ py: 1.35 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <QueryStatsOutlinedIcon sx={{ fontSize: 15 }} />
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.92rem' }}>
                              {stat.model}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.35 }}>{stat.count || 0}</TableCell>
                        <TableCell align="right" sx={{ py: 1.35 }}>
                          {formatMinutes(stat.total_audio_minutes)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.35 }}>
                          {formatSeconds(stat.avg_audio_duration)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.35 }}>
                          {formatSeconds(stat.avg_wait_time)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.35 }}>
                          {formatSeconds(stat.avg_processing_time)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.35 }}>
                          {formatSeconds(stat.seconds_per_audio_minute)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.35 }}>
                          {formatFactor(stat.realtime_factor)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.common.white, 0.76),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Die Kennzahlen basieren auf gespeicherten Job-Protokollen.
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      )}
    </Box>
  );
}
